import { Box, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useEffect, useState } from "react";
import { navigationData, PaginationObj } from "../types";
import { RootStateType } from "../../../redux-store/reducers";
import BreadCrum from "../../../lib/BreadCrum";
import { NO_DATA_AVAILABLE, USER_ROLES } from "../../../utils/constant";
import TablePaginationComponent from "../common/customTable/TablePaginationComponent";
import GroupHeading from "./GroupHeading";
import Card from "./Card";
import SkeletonLoading from "./SkeletonLoading";
import { LatestNotificationsRes, NotificationObj } from "../../../redux-store/types/investorProfile";
import { getLatestNotifications } from "../../../redux-store/actions/investorProfile";
import dayjs from "dayjs";

interface NotificationDataObj {
    heading: string;
    data: Array<NotificationObj>
}

export default function Notifications(): JSX.Element {
    const theme = useTheme()
    const history = useHistory();
    const dispatch = useDispatch()

    //State
    const [notificationData, setNotificationData] = useState<Array<NotificationDataObj>>([])
    const [paginationObj, setPaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [isLoading, setIsLoading] = useState<boolean>(false)

    // Data from redux store
    const { role = '' } = useSelector((state: RootStateType) => state.auth);
    const { selectedPortfolio } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { panNo = "" } = useSelector((store: RootStateType) => store.id);

    const { pathname: pathName, state } = history.location
    const { clientCode = '' } = state as navigationData

    const initialRouter = pathName.split('/').filter((item) => item !== '')

    const rmRouterPath = [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: `${initialRouter[1]} ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: { clientCode }
        },
        {
            name: 'Notifications',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: { clientCode }
        }
    ]

    const investorRouterPath = [
        {
            name: `Dashboard ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'Notifications',
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: { clientCode },
        }
    ]

    const modifyNotificationData = (data: Array<NotificationObj>) => {
        const todayDate = dayjs().startOf("day");
        const yesterdayDate = todayDate.subtract(1, "day");

        const groupedMap = data
            .reduce((
                acc: Map<string, Array<NotificationObj>>,
                item: NotificationObj
            ) => {
                const itemDate = dayjs(Number(item?.timestamp ?? "")).startOf("day")

                let heading = "";

                if (itemDate.isSame(todayDate)) {
                    heading = "Today";
                } else if (itemDate.isSame(yesterdayDate)) {
                    heading = "Yesterday";
                } else {
                    heading = itemDate.format("DD MMM, YY");
                }

                let group = acc.get(heading)

                if (!group) {
                    group = []
                    acc.set(heading, group)
                }

                group.push(item)

                return acc;

            }, new Map());


        // Converting Map to Array
        const groupedArray = Array.from(groupedMap, ([heading, data]) => ({
            heading,
            data,
        }));

        // Sorting items (descending order) in each date section
        groupedArray.forEach((section) => {
            section.data.sort(
                (a: NotificationObj, b: NotificationObj) => Number(b.timestamp) - Number(a.timestamp)
            );
        });

        // Sorting date sections (descending order)
        groupedArray.sort((a, b) => {
            const getDateValue = (heading: string) => {
                if (heading === "Today") return todayDate.valueOf();
                if (heading === "Yesterday") return yesterdayDate.valueOf();
                return dayjs(heading, "DD MMM, YY").startOf("day").valueOf();
            };

            return getDateValue(b.heading) - getDateValue(a.heading);
        });

        return groupedArray
    }

    const fetchNotificationData = async ({
        currentPage,
        rowsPerPage
    }: {
        currentPage: number;
        rowsPerPage: number;
    }) => {
        setIsLoading(true)

        try {
            const payload = {
                clientCode,
                portfolioId: selectedPortfolio?.value ?? "",
                limit: rowsPerPage,
                pan: panNo,
                currentPage: currentPage
            }

            const notificationsRes = await (dispatch(getLatestNotifications(payload)) as unknown as Promise<LatestNotificationsRes>)

            if (notificationsRes) {

                const notificationsArr = notificationsRes?.notifications ?? []

                const modifiedNotificationsArr = modifyNotificationData(notificationsArr)

                setNotificationData(modifiedNotificationsArr ?? [])

                setPaginationObj({
                    currentPage: currentPage - 1,
                    rowsPerPage,
                    totalRows: Number(notificationsRes?.count ?? "")
                })
            }
        } catch (e) {
            console.log("Error: ", e)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            await fetchNotificationData({
                currentPage: 1,
                rowsPerPage: 5,
            })
        }

        fetchData()
    }, [])

    const handlePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        await fetchNotificationData({
            currentPage: page + 1,
            rowsPerPage: paginationObj.rowsPerPage
        })
    }

    const handleRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        await fetchNotificationData({
            currentPage: 1,
            rowsPerPage: Number(event.target.value)
        })
    }

    return (
        <Box>
            <BreadCrum array={[USER_ROLES.RM, USER_ROLES.OPE].includes(role) ? rmRouterPath : investorRouterPath} />
            <Typography
                variant="h1"
                sx={{
                    color: theme.palette.text.primary,
                    marginTop: "25px"
                }}
            >
                Notifications
            </Typography>
            <Box
                sx={{
                    marginTop: "25px",
                    borderRadius: "15px",
                    backgroundColor: theme.palette.common.white,
                    height: `calc(100vh - 210px)`,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                }}
            >
                {isLoading && <SkeletonLoading />}
                {!isLoading && (
                    <>
                        {((notificationData ?? []).length === 0) && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100%"
                                }}
                            >
                                <Typography
                                    variant="body_600"
                                    sx={{
                                        textAlign: 'center',
                                        fontWeight: 500,
                                        color: theme.palette.text.primary
                                    }}
                                >
                                    {NO_DATA_AVAILABLE}
                                </Typography>
                            </Box>
                        )}
                        {((notificationData ?? []).length > 0) && (
                            <>
                                <Box
                                    sx={{
                                        flex: 1,
                                        overflowY: "auto"
                                    }}
                                >
                                    {notificationData.map(group => (
                                        <Box key={group.heading}>
                                            <GroupHeading heading={group.heading} />
                                            {group.data.map((cardData, index) => (
                                                <Card
                                                    key={`${cardData.timestamp}-${index}`}
                                                    {...cardData}
                                                />
                                            ))}
                                        </Box>
                                    ))}
                                </Box>
                                <Box
                                    sx={{
                                        padding: "5px",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        borderTop: `1px solid ${theme.palette.text.secondary}B2`,
                                        backgroundColor: theme.palette.common.white,
                                        position: "sticky",
                                        bottom: 0,
                                        zIndex: 1
                                    }}
                                >
                                    <TablePaginationComponent
                                        tableDataCount={paginationObj.totalRows}
                                        handlePageChange={handlePageChange}
                                        handleRowsPerPageChange={handleRowsPerPageChange}
                                        rowsPerPageOptions={[5, 10, 15, 20]}
                                        rowsPerPage={paginationObj.rowsPerPage}
                                        currentPage={paginationObj.currentPage}
                                    />
                                </Box>
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}